import React from 'react';
import _ from 'lodash';

import {
  FormInputProps,
  LazyAutocompleteField,
  ILazyAutocompleteFieldProps,
} from '@occc-app/design-system';
import { useChainId } from '@occc-app/Chain/hooks';
import {
  mapRawSegmentResponseItemToAutocompleteOption,
  useGetSegmentsLazy,
} from '@occc/feat-segments';
import { SegmentFragment, SegmentsQueryVariables } from '@occc/graphql';

type SegmentSelectFieldProps = {
  name: string;
  label?: string;
  inputProps?: FormInputProps;
  idExcludes?: string[];
};

const makeFilter = ({
  chainId,
  search,
  idExcludes,
}: {
  chainId: string;
  search?: string;
  idExcludes?: string[];
}) => {
  const filter: NonNullable<SegmentsQueryVariables>['filter'] = {
    isDeleted: false,
    idExcludes,
  };
  const setFilterValue = (propName: string, propValue: any) =>
    propValue ? _.set(filter, propName, propValue) : null;
  setFilterValue('chainIdEquals', chainId);
  setFilterValue('search', search);
  return filter;
};

const mapQueryResponseToRawArray = (response: ReturnType<typeof useGetSegmentsLazy>['response']) =>
  response.data || [];

export const SegmentSelectField: React.FC<SegmentSelectFieldProps> = ({ idExcludes, ...props }) => {
  const chainId = useChainId();
  const makeFilterMemo = React.useCallback<
    NonNullable<
      ILazyAutocompleteFieldProps<
        SegmentFragment,
        ReturnType<typeof useGetSegmentsLazy>['response']
      >['makeQueryFilter']
    >
  >((search) => makeFilter({ chainId, search, idExcludes }), [chainId, idExcludes]);

  return (
    <LazyAutocompleteField<SegmentFragment, ReturnType<typeof useGetSegmentsLazy>['response']>
      label='Segments'
      idExcludes={idExcludes}
      {...props}
      useLazyQuery={useGetSegmentsLazy}
      makeQueryFilter={makeFilterMemo}
      mapQueryResponseToRawArray={mapQueryResponseToRawArray}
      mapRawResponseItemToAutocompleteOption={mapRawSegmentResponseItemToAutocompleteOption}
    />
  );
};
